import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import Flatpickr from "react-flatpickr";

import {
  useCalendarOptions,
  formatDate,
  useResponsiveMonths,
  replaceYearInputWithSelect,
} from "./useCalendar";
import { resolveOptionLabel, FieldOption } from "../form-item/optionLabel";

export type DateInputValue = Date | Date[] | null;

export interface DateInputChangeEvent {
  target: {
    name?: string;
    value: DateInputValue;
    /** バリデーションエラー（常に渡されるが、呼び出し側の型注釈を緩くするため optional） */
    error?: string | null;
  };
}

export interface DateInputProps {
  label?: string;
  /** ラベル右脇の補助テキスト（必須 / 任意）。 */
  option?: FieldOption | string;
  placeholder?: string;
  width?: string;
  mode?: "single" | "range" | "multiple";
  value?: Date | Date[] | string | null;
  /**
   * 変更ハンドラ。single モードでは `e.target.value` は `Date | null`。
   * メソッド構文（bivariant）にして、呼び出し側がモードに応じた
   * 狭い型（`{ target: { value: Date } }` 等）で注釈しても通るようにしている。
   */
  onChange?(e: DateInputChangeEvent): void;
  maxDate?: Date | string;
  minDate?: Date | string;
  name?: string;
  className?: string;
  dateFormat?: string;
  allowInput?: boolean;
  required?: boolean;
  yearRange?: [number, number];
  options?: Record<string, unknown>;
  /**
   * コントロールの密度。
   * - `"medium"`（既定）: Input と同じ縦サイズのフォーム用
   * - `"small"`: テーブルセル内などの省スペース用。枠線なし（ホバー / フォーカス時に表示）
   */
  size?: "medium" | "small";
}

const StyledIcon = styled.div.attrs({
  className: "material-symbols-rounded",
})<{ $small: boolean }>`
  position: absolute;
  top: 50%;
  right: ${({ $small }) => ($small ? "4px" : "8px")};
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
  font-size: ${({ $small }) => ($small ? "18px" : "24px")};
  color: var(--soremo-light-gray);
`;

const CalendarContainer = styled.div`
  position: relative;
  width: 100%;
`;

// エラーメッセージ表示用
const ErrorMessage = styled.div`
  color: var(--soremo-green);
  font-size: 12px;
  margin-top: 4px;
  padding-left: 4px;
`;

// Inputと同じスタイルを適用したFlatpickr（small はテーブルセル内向けの高密度サイズ）。
// flatpickr が input に type="text" を付与し、グローバルの input[type="text"] ルール
// （詳細度 0,1,1）が styled の単一クラスに勝つため、&& で詳細度を上げている。
const StyledFlatpickr = styled(Flatpickr)<{ $error: boolean; $small: boolean }>`
  && {
    /* inline-block のベースライン余白で親セルが間延びしないよう block にする */
    display: block;
    width: 100%;
    padding: ${({ $small }) =>
      $small ? "2px 22px 2px 6px" : "12px 4px 12px 12px"};
    margin: 0;
    border: 1px solid
      ${({ $error, $small }) =>
        $error
          ? "var(--soremo-green)"
          : $small
            ? "transparent"
            : "var(--soremo-border)"};
    border-radius: 4px;
    font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
    ${({ $small }) =>
      $small ? "background: transparent; font-size: 13px; line-height: 150%;" : ""}
  }

  /* small はテーブルセル内用のため自前の枠を持たない（選択中の表示は
   * セル側の :focus-within リングに任せる） */
  &&:focus {
    border-color: ${({ $error, $small }) =>
      $error
        ? "var(--soremo-green)"
        : $small
          ? "transparent"
          : "var(--soremo-blue)"};
    outline: none;
    box-shadow: ${({ $error, $small }) =>
      $small
        ? "none"
        : `2px 4px 8px 3px ${
            $error ? "rgba(255, 59, 48, 0.1)" : "rgba(0, 154, 206, 0.1)"
          }`};
    /* グローバルの input:focus { font-size: 16px }（SP ズーム回避）による
     * 高さジャンプを small では防ぐ */
    ${({ $small }) => ($small ? "font-size: 13px;" : "")}
  }
`;

/**
 * 入力フィールド型のカレンダーコンポーネント
 */
const DateInput: React.FC<DateInputProps> = ({
  label,
  option = "",
  placeholder,
  width = "100%",
  mode = "single",
  value,
  onChange,
  maxDate,
  minDate,
  name,
  className,
  dateFormat,
  allowInput = false,
  required = false,
  yearRange,
  options: additionalOptions = {},
  size = "medium",
}) => {
  const [error, setError] = useState<string | null>(null);

  // レスポンシブな月表示数を取得。ただし単一日選択（single）は範囲選択ではないため
  // 常に1ヶ月表示にする（2ヶ月並べる必要があるのは range のみ）。
  const responsiveMonths = useResponsiveMonths();
  const showMonths = mode === "single" ? 1 : responsiveMonths;

  // flatpickrインスタンスへの参照
  const flatpickrRef = useRef<Flatpickr | null>(null);

  const validateDate = useCallback(
    (date: DateInputValue): string | null => {
      if (required && !date) {
        return "日付を選択してください";
      }
      if (date && minDate && new Date(date as Date) < new Date(minDate)) {
        return `${formatDate(minDate)}以降の日付を選択してください`;
      }
      if (date && maxDate && new Date(date as Date) > new Date(maxDate)) {
        return `${formatDate(maxDate)}以前の日付を選択してください`;
      }
      return null;
    },
    [required, minDate, maxDate],
  );

  const { text: optionLabel, className: optionClass } = resolveOptionLabel(option);
  const containerClass =
    width === "100%" ? "u-col u-gap4 u-w100 u-relative" : "u-col u-fill u-relative";

  // flatpickr は空文字や Invalid Date を渡すと "Invalid date provided" を投げる。
  // 未入力（"" / null / undefined）や不正な Date は undefined（＝未選択）に正規化する。
  const normalizedValue =
    value == null ||
    value === "" ||
    (value instanceof Date && Number.isNaN(value.getTime()))
      ? undefined
      : value;

  // カレンダーオプションの設定
  const options = useCalendarOptions({
    mode,
    inline: false,
    minDate,
    maxDate,
    allowInput,
    dateFormat,
    yearRange,
    additionalOptions: {
      ...additionalOptions,
      showMonths,
    },
  });

  // showMonthsが変更されたときにflatpickrを更新
  useEffect(() => {
    if (flatpickrRef.current && flatpickrRef.current.flatpickr) {
      const fp = flatpickrRef.current.flatpickr;
      fp.set("showMonths", showMonths);

      setTimeout(() => {
        const calendar = fp.calendarContainer;
        if (calendar) {
          if (showMonths > 1) {
            calendar.classList.add("multiMonth");
          } else {
            calendar.classList.remove("multiMonth");
          }
        }
        const currentYear = new Date().getFullYear();
        const [minY, maxY] = yearRange || [currentYear - 100, currentYear + 10];
        replaceYearInputWithSelect(fp, minY, maxY);
      }, 0);
    }
  }, [showMonths, yearRange]);

  // 日付変更時のハンドラ
  const handleChange = useCallback(
    (dates: Date[]) => {
      const newValue: DateInputValue =
        mode === "range" || mode === "multiple" ? dates : (dates[0] ?? null);
      const validationError = validateDate(newValue);
      setError(validationError);

      if (onChange) {
        onChange({
          target: {
            name,
            value: newValue,
            error: validationError,
          },
        });
      }
    },
    [mode, name, onChange, validateDate],
  );

  return (
    <div className={`${containerClass} ${className || ""}`} style={{ width }}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
      )}
      <div className="u-row u-w100 u-relative">
        <CalendarContainer>
          <StyledFlatpickr
            ref={flatpickrRef}
            options={options}
            value={normalizedValue}
            onChange={handleChange}
            placeholder={placeholder}
            $error={!!error}
            $small={size === "small"}
          />
          <StyledIcon $small={size === "small"}>
            {mode === "range" ? "date_range" : "event"}
          </StyledIcon>
        </CalendarContainer>
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

export default React.memo(DateInput, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.minDate === nextProps.minDate &&
    prevProps.maxDate === nextProps.maxDate &&
    prevProps.mode === nextProps.mode &&
    prevProps.required === nextProps.required &&
    prevProps.size === nextProps.size
  );
});
